import { useMutation, useQuery } from "@tanstack/react-query";
import { Select, Spin, message } from "antd";
import { StyledButton } from "common/components/StyledElements";
import APIConstants from "common/constants/APIConstants";
import { useAuthContext } from "common/contexts/AuthContext";
import { api } from "common/utils/APIMethods";
import { useEffect, useState } from "react";
import styled from "styled-components";

export const selectProductsModalProps = {
  title: "Assign Inventory",
  width: "65%",
  centered: true,
  footer: null
};

// Styled Components
const ModalContainer = styled.div`
  // padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SelectContainer = styled.div`
  //  margin-bottom: 10px;
`;

const SelectedProductsContainer = styled.div`
  border: 1px solid #e8e9ea;
  border-radius: 8px;
  overflow: hidden;
  // margin-bottom: 20px;
`;

const ProductsList = styled.div`
  max-height: 450px;
  overflow-y: auto;
  padding: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }
`;

const ProductItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  margin-bottom: 8px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e8e9ea;
`;

const ProductName = styled.div`
  font-weight: 500;
  color: #333;
  flex: 1;
`;

const QuantityContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 150px;
  justify-content: flex-end;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #999;
  background: #fafafa;
  border-radius: 8px;

  div:first-child {
    font-size: 14px;
    margin-bottom: 8px;
    font-weight: 500;
  }

  div:last-child {
    font-size: 13px;
    color: #aaa;
  }
`;

const Footer = styled.div`
  text-align: right;
  padding-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  border-top: 1px solid #f0f0f0;
`;

const SelectProductsModal = ({ onSuccessCallback, selectedProduct }) => {
  const { authConfig } = useAuthContext();
  const [selectedProducts, setSelectedProducts] = useState([]);

  const {

    productMappingInventory,
    saveProductMapping
  } = APIConstants;

  const { data: products, isLoading: isProductsLoading } = useQuery({
    queryKey: ["Inventory"],
    queryFn: () => api({ url: productMappingInventory }, authConfig)
  });

  useEffect(() => {
    if (selectedProduct?.inventories) {
      const existingProducts = selectedProduct.inventories.map((p) => ({
        id: p?.id,
        name: p?.materialName
      }));
      setSelectedProducts(existingProducts);
    }
  }, [selectedProduct]);

  const handleProductChange = (selectedIds) => {
    const currentIds = selectedProducts.map((p) => p.id);
    const addedIds = selectedIds.filter((id) => !currentIds.includes(id));
    const removedIds = currentIds.filter((id) => !selectedIds.includes(id));

    let updatedProducts = [...selectedProducts];

    addedIds.forEach((id) => {
      const product = products?.find((p) => p.id === id);
      if (product) {
        updatedProducts.push({
          id,
          quantity: 1,
          name: product.materialName
        });
      }
    });

    updatedProducts = updatedProducts.filter((p) => !removedIds.includes(p.id));
    setSelectedProducts(updatedProducts);
  };

  const { mutate: assignProductsMutate, isPending } = useMutation({
    mutationFn: (body) =>
      api({ url: saveProductMapping, method: "PUT", body }, authConfig),

    onSuccess: () => {
      message.success("Inventories assigned successfully!");
      onSuccessCallback?.();
    },

    onError: () => {
      message.error("Failed to assign inventory");
    }
  });

  const handleAssignProducts = () => {
    const selectedProductId = selectedProduct?.id || selectedProduct?.key;

    if (!selectedProductId) {
      message.error("Product info missing");
      return;
    }

    if (selectedProducts.length === 0) {
      message.error("Please select at least one product");
      return;
    }

    const body = {
      productId: selectedProductId,
      inventories: selectedProducts.map((p) => p.id)
    };

    assignProductsMutate(body);
  };

  const productOptions =
    products?.map((p) => ({
      label: p.materialName,
      value: p.id
    })) ?? [];

  return (
    <ModalContainer>
      {isProductsLoading ? (
        <Spin
          size="large"
          style={{ display: "flex", justifyContent: "center", padding: "40px" }}
        />
      ) : (
        <>
          <SelectContainer>
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Select products"
              style={{ width: "100%" }}
              value={selectedProducts.map((p) => p.id)}
              onChange={handleProductChange}
              loading={isPending}
              optionFilterProp="label"
              options={productOptions}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              maxTagCount={2}
            />
          </SelectContainer>

          <SelectedProductsContainer>
            {selectedProducts.length > 0 ? (
              <ProductsList>
                {selectedProducts.map((product) => (
                  <ProductItem key={product.id}>
                    <ProductName>
                      {product.name || `Product ${product.id}`}
                    </ProductName>
                    <QuantityContainer>
                      <StyledButton
                        type="text"
                        danger
                        onClick={() => {
                          setSelectedProducts(
                            selectedProducts.filter((p) => p.id !== product.id)
                          );
                        }}
                        size="small"
                        style={{
                          padding: "2px 12px",
                          backgroundColor: "red",
                          color: "white"
                        }}
                      >
                        Remove
                      </StyledButton>
                    </QuantityContainer>
                  </ProductItem>
                ))}
              </ProductsList>
            ) : (
              <EmptyState>
                <div>No inventory selected yet</div>
                <div>Select Inventory from the dropdown above</div>
              </EmptyState>
            )}
          </SelectedProductsContainer>

          <Footer>
            <ProductName>{`${selectedProducts.length} inventories selected `}</ProductName>
            <StyledButton
              type="primary"
              isLoading={isPending}
              onClick={handleAssignProducts}
              disabled={isPending || selectedProducts.length === 0}
              style={{
                padding: "8px 24px",
                height: "auto",
                fontSize: "14px",
                fontWeight: "600"
              }}
            >
              {isPending ? "Assigning..." : "Assign Inventory"}
            </StyledButton>
          </Footer>
        </>
      )}
    </ModalContainer>
  );
};

export default SelectProductsModal;
